import { cn } from '@nexelem/utils';
import { cva, VariantProps } from 'class-variance-authority';
import { PropsWithChildren, ReactNode } from 'react';
import { useSortable } from '@dnd-kit/react/sortable';
import { DragDropProvider, KeyboardSensor, PointerSensor } from '@dnd-kit/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines } from '@fortawesome/pro-light-svg-icons';
import { move } from '@dnd-kit/helpers';
import { IconButton } from '../IconButton';

const Root = ({ children }: PropsWithChildren) => <div className="flex flex-col gap-2">{children}</div>;

type SortableItemProps = {
  sortable: true;
  id: string | number;
  index: number;
  /** Isolates this item into a sortable group; items from other groups cannot be dropped here. */
  group?: string;
};

type NonSortableItemProps = {
  sortable?: false;
  id?: never;
  index?: never;
  group?: never;
};

type CommonProps = {
  Actions?: ReactNode;
  className?: string;
};

const itemVariants = cva(
  'flex items-center rounded-xs border border-border-container bg-surface-secondary px-3 text-t16-sb text-text-action focus-visible:outline-border-focus-keyboard',
  {
    variants: {
      hiddenState: {
        true: 'text-text-secondary-darker',
      },
      hoverable: {
        true: 'hover:border-border-hover-lighter hover:bg-surface-hover-lighter hover:text-text-hover',
      },
    },
  },
);

type ItemProps = CommonProps & VariantProps<typeof itemVariants> & (SortableItemProps | NonSortableItemProps);

const Item = ({
  children,
  Actions,
  hiddenState,
  hoverable,
  className,
  id = 0,
  index = 0,
  group,
  sortable,
}: PropsWithChildren<ItemProps>) => {
  const { ref, handleRef } = useSortable({
    id,
    index,
    disabled: !sortable,
    ...(group ? { group, type: group, accept: group } : {}),
  });
  return (
    <div
      className={cn(itemVariants({ hiddenState, hoverable }), Actions && 'pr-0', sortable && 'pl-0', className)}
      ref={ref}
    >
      {sortable && <IconButton className="cursor-grab" Icon={<FontAwesomeIcon icon={faGripLines} />} ref={handleRef} />}
      <span className="mr-auto py-2">{children}</span>
      {Actions}
    </div>
  );
};

const ListDragDropProvider: typeof DragDropProvider = (props) => (
  <DragDropProvider sensors={[PointerSensor, KeyboardSensor]} {...props} />
);

export const List = {
  Root,
  Item,
  DragDropProvider: ListDragDropProvider,
  move,
};
